import type { FileModel } from '../models/api/FileModel';
import { error } from '@sveltejs/kit';
import { snackbar } from '$lib/stores';
import { t } from 'svelte-i18n';
import { get } from 'svelte/store';

export const downloadFile = async (document: FileModel): Promise<void> => {
	const $t = get(t);

	let uri;
	if (document.url?.includes('http')) {
		const url = new URL(document.url);
		uri = url.pathname + url.search;
	} else {
		uri = document.url;
	}
	const res = await fetch(uri, {
		headers: { Accept: '*/*', responseType: 'blob', 'content-type': document.mimeType }
	});
	if (res.ok) {
		const blob = await res.blob();
		downloadFromResponse(blob, document);
	} else {
		const json = await res.json();
		const resStatus = res.status;
		if (resStatus === 404) {
			snackbar.danger($t('fileNotFound'));
		} else {
			snackbar.danger(json.message ?? $t('register.errors.default'));
		}
		error(resStatus, json);
	}
};

export const downloadFromResponse = (blob: Blob, file: FileModel): void => {
	const link = document.createElement('a');
	link.download = file.fileName;
	link.href = URL.createObjectURL(new Blob([blob]));
	document.body.appendChild(link);
	link.click();
	URL.revokeObjectURL(link.href);
	document.body.removeChild(link);
};

export const isFileTypeAccepted = (input: any, file: File): boolean => {
	return input.accept?.indexOf(file?.type?.split('/').pop()) > -1;
};

export const readBuffer = async (file: File, start = 0, end = 2) => {
	const reader = new FileReader();
	const readPromise = new Promise<ArrayBuffer>((resolve, reject) => {
		reader.onload = () => resolve(reader.result as ArrayBuffer);
		reader.onerror = () => reject(new Error('Error reading file'));
	});
	reader.readAsArrayBuffer(file.slice(start, end));
	return readPromise;
};

const arrayFormatFile = [
	{
		mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		pattern: [0x50, 0x4b, 0x03, 0x04]
	},
	{
		mime: 'application/msword',
		pattern: [0xd0, 0xcf, 0x11, 0xe0]
	},
	{
		mime: 'application/vnd.oasis.opendocument.text',
		pattern: [0x50, 0x4b, 0x03, 0x04]
	},
	{
		mime: 'application/pdf',
		pattern: [0x25, 0x50, 0x44, 0x46]
	},
	{
		mime: 'image/png',
		pattern: [0x89, 0x50, 0x4e, 0x47]
	},
	{
		mime: 'image/jpeg',
		pattern: [0xff, 0xd8, 0xff]
	},
	{
		mime: 'image/gif',
		pattern: [0x47, 0x49, 0x46, 0x38]
	},
	{
		mime: 'image/heic',
		pattern: [0x00, 0x00, 0x00, 0xc8]
	},
	{
		mime: 'image/heif',
		pattern: [0x00, 0x00, 0x00, 0x18]
	}
];

export const identifyFileFormat = (byteValues: number[]) => {
	for (const format of arrayFormatFile) {
		const pattern = format.pattern;
		if (isPatternMatch(pattern, byteValues)) {
			return format.mime;
		}
	}
	return null;
};

const isPatternMatch = (pattern: number[], byteValues: number[]) => {
	for (let i = 0; i < pattern.length; i++) {
		if (pattern[i] !== byteValues[i]) {
			return false;
		}
	}
	return true;
};

export const fileByteFormatIsAccepted = async (input: any, file: File): Promise<boolean> => {
	const fileHeader = await readBuffer(file, 0, 8);
	const headerBytes = new Uint8Array(fileHeader);
	const byteValuesArray = Object.values(headerBytes);
	const identifiedFormat = identifyFileFormat(byteValuesArray);
	return identifiedFormat && input.accept?.includes(identifiedFormat);
};
